/**
 * Реестр раскладок разворота и вывод формата картинки (01-SPEC.md §3.10).
 *
 * Держится на разделении двух независимых осей (§3.10.1):
 * - **раскладка** задаёт площадь картинки — и, значит, её размер и ориентацию;
 * - **положение текста** не задаёт ничего.
 *
 * Инвариант гарантируется сигнатурой: `imageSpecFor` не принимает текстовые блоки.
 */
import { defaultBookAttributes, pageSizeFor, spreadSizeFor, } from './attributes.js';
import { mmToPx, mmToPxCeil, roundTo } from './units.js';
import { capacityForSlot, fontMetricsFor } from './text-metrics.js';
import { slotId } from './text-block.js';
/**
 * Отступ текстовой плашки от края под обрез, мм.
 *
 * Калибровка: в свёрстанной книге (`max-book`) самая длинная строка занимает
 * ~146.5 мм из 148.5 мм половины разворота, то есть фактические поля там ещё
 * меньше. Для более свободной вёрстки и запаса под обрез поле увеличено
 * до 10 мм; геометрия слота и расчёт вместимости используют одну константу.
 *
 * ⚠️ Замером подтверждён **только** `full-bleed` — единственная раскладка,
 * которая существует в свёрстанном виде. Для `split` и `band` это значение
 * переносится по геометрии, то есть является экстраполяцией, а не измерением;
 * перекалибровать на первых реальных книгах (01-SPEC.md §3.10.6).
 */
export const TEXT_SAFE_MARGIN_MM = 10;
/**
 * Доля полезной высоты страницы, которую занимает текстовая плашка поверх
 * картинки в раскладке `full-bleed`: текст лежит в углу и не должен закрывать
 * иллюстрацию. Доля высоты сохранена из свёрстанной книги; при поле 10 мм
 * в слот входит 7 строк при 15 pt или 8 строк при автоподборе до 14 pt.
 */
const FULL_BLEED_OVERLAY_HEIGHT_FRACTION = 0.42;
/**
 * Типографский предел длины строки в раскладке `band`: полоса шириной
 * в разворот даёт строки под сотню символов, что нечитаемо для детской книги,
 * поэтому текст центрируется в колонке примерно на 65 символов (01-SPEC.md §3.10.6).
 *
 * ⚠️ Это классическая типографская норма (45–75 знаков в строке), а не замер
 * по свёрстанной книге: раскладки `band` в `max-book` не существует.
 * Подлежит перекалибровке на первых реальных книгах.
 */
const BAND_READABLE_LINE_CHARS = 65;
/** Допуск на соотношение сторон при подгонке под размеры модели (01-SPEC.md §3.10.3). */
export const MODEL_SIZE_ASPECT_TOLERANCE = 0.02;
/**
 * Допуск на расхождение соотношения сторон, после которого картинка помечается
 * `imageStale` (01-SPEC.md §3.10.5).
 *
 * Это **не тот же допуск**, что `MODEL_SIZE_ASPECT_TOLERANCE`, и совпадать они
 * не обязаны. У `snapToModelSize` 2 % — это точность, с которой мы соглашаемся
 * исказить кадр при запросе к модели, то есть цена ошибки нулевая. Здесь 5 % —
 * порог, за которым уже сгенерированная картинка объявляется негодной, и цена
 * ошибки — перегенерация за деньги пользователя (автоперегенерации нет,
 * но флаг гонит его на кнопку). Поэтому порог сознательно шире.
 *
 * Три опорных случая (01-SPEC.md §3.10.5, DoD E1-T3):
 * `split` ↔ `full-bleed` (0.705 ↔ 1.414) — устарела; `bandImageRatio`
 * 0.7 → 0.5 (2.020 → 2.829, 40 %) — устарела; 0.7 → 0.72 (2.020 → 1.964,
 * 2.8 %) — нет.
 */
export const IMAGE_STALE_ASPECT_TOLERANCE = 0.05;
/** Все раскладки разворота (01-SPEC.md §3.10.2). */
export const SPREAD_LAYOUT_IDS = ['split', 'full-bleed', 'band'];
const LAYOUT_DEFINITIONS = {
    split: {
        id: 'split',
        nameRu: 'Страница текста + страница картинки',
        image: {
            area: 'one-page',
            orientation: 'portrait',
            overlaid: false,
            bleed: true,
            bleedEdges: { horizontal: 1, vertical: 2 },
        },
        textSlots: 1,
        allowedPositions: ['left', 'right'],
        textSlot: { widthBasis: 'page', heightBasis: 'page', heightFraction: 1 },
        promptHintEn: 'vertical portrait composition, single-page illustration, subject centered, full frame',
    },
    'full-bleed': {
        id: 'full-bleed',
        nameRu: 'Картинка на весь разворот',
        image: {
            area: 'whole-spread',
            orientation: 'landscape',
            overlaid: true,
            bleed: true,
            bleedEdges: { horizontal: 2, vertical: 2 },
        },
        textSlots: 2,
        allowedPositions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
        textSlot: {
            widthBasis: 'half-spread',
            heightBasis: 'page',
            heightFraction: FULL_BLEED_OVERLAY_HEIGHT_FRACTION,
        },
        promptHintEn: 'wide landscape composition; keep main characters away from the vertical center line (book gutter); leave calm low-detail areas near the corners for text overlay; nothing important near the edges',
    },
    band: {
        id: 'band',
        nameRu: 'Широкая картинка + полоса текста',
        image: {
            area: 'band',
            orientation: 'landscape',
            overlaid: false,
            bleed: true,
            bleedEdges: { horizontal: 2, vertical: 1 },
        },
        textSlots: 1,
        allowedPositions: ['top', 'bottom'],
        textSlot: {
            widthBasis: 'spread',
            heightBasis: 'band-strip',
            heightFraction: 1,
            maxCharsPerLineCap: BAND_READABLE_LINE_CHARS,
        },
        promptHintEn: 'wide panoramic composition, subject centered, generous headroom, nothing important near the edges',
    },
};
/** Доля высоты разворота под картинку в раскладке `band` для конкретного разворота. */
export function resolveBandImageRatio(attributes, scene) {
    return scene?.bandImageRatio ?? attributes.layout.bandImageRatio;
}
/** Полезная геометрия одного текстового слота раскладки, мм (без безопасных полей). */
export function textSlotFor(layoutId, attributes, scene) {
    const layout = LAYOUT_DEFINITIONS[layoutId];
    const page = pageSizeFor(attributes);
    const spread = spreadSizeFor(attributes);
    const margin = TEXT_SAFE_MARGIN_MM;
    const baseWidthMm = layout.textSlot.widthBasis === 'page'
        ? page.widthMm
        : layout.textSlot.widthBasis === 'half-spread'
            ? spread.widthMm / 2
            : spread.widthMm;
    const baseHeightMm = layout.textSlot.heightBasis === 'band-strip'
        ? spread.heightMm * (1 - resolveBandImageRatio(attributes, scene))
        : page.heightMm;
    return {
        widthMm: roundTo(Math.max(0, baseWidthMm - 2 * margin), 3),
        heightMm: roundTo(Math.max(0, (baseHeightMm - 2 * margin) * layout.textSlot.heightFraction), 3),
    };
}
/**
 * Вместимость текста в слоте раскладки — «строк × символов в строке».
 * Считается из геометрии слота и метрик шрифта (01-SPEC.md §3.10.6).
 */
export function textCapacityFor(layoutId, attributes, sizePt = attributes.typography.baseSizePt, scene) {
    const layout = LAYOUT_DEFINITIONS[layoutId];
    const metrics = fontMetricsFor(attributes.typography.fontFamily);
    return capacityForSlot(textSlotFor(layoutId, attributes, scene), metrics, sizePt, layout.textSlot.maxCharsPerLineCap);
}
const REFERENCE_ATTRIBUTES = defaultBookAttributes();
/** Закрытый реестр раскладок разворота (01-SPEC.md §3.10.2). */
export const SPREAD_LAYOUTS = Object.freeze(Object.fromEntries(SPREAD_LAYOUT_IDS.map((id) => [
    id,
    Object.freeze({
        ...LAYOUT_DEFINITIONS[id],
        textCapacity: textCapacityFor(id, REFERENCE_ATTRIBUTES),
    }),
])));
/** Раскладка по идентификатору. */
export function spreadLayout(layoutId) {
    return SPREAD_LAYOUTS[layoutId];
}
/** Допустимые положения текста для раскладки (01-SPEC.md §3.10.2). */
export function allowedTextPositions(layoutId) {
    return SPREAD_LAYOUTS[layoutId].allowedPositions;
}
export function isTextPositionAllowed(layoutId, position) {
    return allowedTextPositions(layoutId).includes(position);
}
/** Раскладка конкретного разворота: точечное переопределение или раскладка книги. */
export function resolveLayout(scene, attributes) {
    return scene?.layoutId ?? attributes.layout.default;
}
/**
 * Сторона текстовой страницы в раскладке `split` (01-SPEC.md §3.10.8).
 * Явное положение первого блока важнее чередования.
 */
export function resolveTextSide(scene, attributes) {
    const explicit = scene.textBlocks?.[0]?.position;
    if (explicit === 'left' || explicit === 'right')
        return explicit;
    if (!attributes.layout.alternateSides)
        return 'left';
    return scene.index % 2 ? 'right' : 'left';
}
/**
 * Формат картинки для раскладки (01-SPEC.md §3.10.3).
 *
 * Зависит только от раскладки, формата книги и (для `band`) `bandImageRatio`.
 * Положение текста в сигнатуру не входит — и не может войти.
 */
export function imageSpecFor(layoutId, attributes, scene) {
    const layout = LAYOUT_DEFINITIONS[layoutId];
    const page = pageSizeFor(attributes);
    const spread = spreadSizeFor(attributes);
    const { bleedMm, dpi } = attributes.format;
    const area = layout.image.area === 'one-page'
        ? { widthMm: page.widthMm, heightMm: page.heightMm }
        : layout.image.area === 'whole-spread'
            ? { widthMm: spread.widthMm, heightMm: spread.heightMm }
            : {
                widthMm: spread.widthMm,
                heightMm: spread.heightMm * resolveBandImageRatio(attributes, scene),
            };
    const widthMm = roundTo(area.widthMm, 3);
    const heightMm = roundTo(area.heightMm, 3);
    const widthPx = mmToPx(widthMm, dpi);
    const heightPx = mmToPx(heightMm, dpi);
    return {
        layoutId,
        orientation: widthMm > heightMm ? 'landscape' : 'portrait',
        aspect: roundTo(widthMm / heightMm, 3),
        widthMm,
        heightMm,
        widthPx,
        heightPx,
        bleedMm,
        bleedWidthPx: widthPx + mmToPxCeil(bleedMm * layout.image.bleedEdges.horizontal, dpi),
        bleedHeightPx: heightPx + mmToPxCeil(bleedMm * layout.image.bleedEdges.vertical, dpi),
    };
}
/** Размер, который запрашивается у модели: в обрез плюс вылеты. */
export function generationSizeOf(spec) {
    return { widthPx: spec.bleedWidthPx, heightPx: spec.bleedHeightPx };
}
/** Разбор размера в формате WaveSpeed — `"1448*2048"`. */
export function parseModelSize(size) {
    const match = /^(\d+)\s*[*x×]\s*(\d+)$/u.exec(size.trim());
    if (!match?.[1] || !match[2])
        return null;
    return { widthPx: Number(match[1]), heightPx: Number(match[2]) };
}
/** Обратное преобразование — `"1448*2048"`. */
export function formatModelSize(size) {
    return `${size.widthPx}*${size.heightPx}`;
}
/**
 * Подгонка размера под ближайший поддерживаемый моделью с сохранением
 * соотношения сторон (01-SPEC.md §3.10.3).
 *
 * Списки размеров моделей WaveSpeed пока не собраны (открытый вопрос §8.7),
 * поэтому **при пустом списке функция возвращает вычисленный размер как есть** —
 * не падает и не подставляет «разумный дефолт».
 */
export function snapToModelSize(size, supportedSizes) {
    if (supportedSizes.length === 0)
        return { ...size };
    const targetAspect = size.widthPx / size.heightPx;
    const targetArea = size.widthPx * size.heightPx;
    let best = null;
    let bestScore = Number.POSITIVE_INFINITY;
    let bestAreaDelta = Number.POSITIVE_INFINITY;
    for (const candidate of supportedSizes) {
        if (candidate.widthPx <= 0 || candidate.heightPx <= 0)
            continue;
        const aspectDelta = Math.abs(candidate.widthPx / candidate.heightPx - targetAspect) / targetAspect;
        const areaDelta = Math.abs(candidate.widthPx * candidate.heightPx - targetArea);
        const withinTolerance = aspectDelta <= MODEL_SIZE_ASPECT_TOLERANCE;
        // Размеры в допуске всегда важнее размеров вне допуска.
        const score = withinTolerance ? 0 : aspectDelta;
        if (score < bestScore || (score === bestScore && areaDelta < bestAreaDelta)) {
            best = candidate;
            bestScore = score;
            bestAreaDelta = areaDelta;
        }
    }
    return best ? { ...best } : { ...size };
}
/** Относительное расхождение двух соотношений сторон. */
function aspectDeviation(a, b) {
    const max = Math.max(a, b);
    return max === 0 ? 0 : Math.abs(a - b) / max;
}
/**
 * Устарел ли формат уже сгенерированной картинки (01-SPEC.md §3.10.5).
 *
 * Смена положения текста сюда не доходит в принципе: сравниваются только
 * соотношения сторон, а они не зависят от текста.
 */
export function isImageStale(scene, layoutId, attributes) {
    const current = scene.imageSpec;
    if (!current)
        return false;
    const next = imageSpecFor(layoutId, attributes, scene);
    return aspectDeviation(current.aspect, next.aspect) > IMAGE_STALE_ASPECT_TOLERANCE;
}
/** Ближайшее допустимое положение текста при переходе в другую раскладку. */
export function nearestTextPosition(position, layoutId, slotIndex) {
    const allowed = allowedTextPositions(layoutId);
    if (position && allowed.includes(position))
        return position;
    const wantsRight = position?.endsWith('right') ?? slotIndex > 0;
    const wantsBottom = position?.startsWith('bottom') ?? slotIndex > 0;
    const fallback = allowed[0];
    /* c8 ignore next */
    if (!fallback)
        throw new Error(`У раскладки ${layoutId} нет допустимых положений текста`);
    switch (layoutId) {
        case 'split':
            return wantsRight ? 'right' : 'left';
        case 'band':
            return wantsBottom ? 'bottom' : 'top';
        case 'full-bleed':
            return `${wantsBottom ? 'bottom' : 'top'}-${wantsRight ? 'right' : 'left'}`;
        /* c8 ignore next 2 */
        default:
            return fallback;
    }
}
/**
 * Перенос текстовых блоков при смене раскладки (01-SPEC.md §3.10.5).
 * Текст не теряется никогда: при сжатии двух слотов в один блоки склеиваются,
 * при обратном переходе второй слот создаётся пустым.
 */
export function remapTextBlocks(blocks, from, to) {
    void from;
    if (blocks.length === 0)
        return [];
    const targetSlots = SPREAD_LAYOUTS[to].textSlots;
    const first = blocks[0];
    function styled(index, text, block) {
        const textColor = block?.textColor ?? first?.textColor;
        const highlightColor = block?.highlightColor ?? first?.highlightColor;
        const result = {
            slotId: slotId(index),
            text,
            position: nearestTextPosition(block?.position ?? first?.position, to, index),
        };
        if (textColor !== undefined)
            result.textColor = textColor;
        if (highlightColor !== undefined)
            result.highlightColor = highlightColor;
        if (block?.manual === true)
            result.manual = true;
        return result;
    }
    const joinTexts = (list) => list
        .map((block) => block.text)
        .filter((value) => value.length > 0)
        .join('\n');
    if (targetSlots === 1) {
        return [styled(0, joinTexts(blocks), blocks.length === 1 ? first : undefined)];
    }
    const result = [];
    for (let index = 0; index < targetSlots; index += 1) {
        const isLast = index === targetSlots - 1;
        const block = blocks[index];
        const text = isLast ? joinTexts(blocks.slice(index)) : (block?.text ?? '');
        result.push(styled(index, text, block));
    }
    return result;
}
//# sourceMappingURL=layouts.js.map